<script setup lang="ts">
import {
    Healthcenter,
    HealthcenterOpening,
    OpeningHourObject
} from "@/core/interfaces/healthcenter/Healthcenter";
import {computed, ref} from "vue";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import {OpeningHoursRequest} from "@/core/interfaces/healthcenter/OpeningHoursRequest";
import {Label} from "@/core/components/ui/label";
import Icon from "@/core/components/icon/Icon.vue";
import HealthcenterOpeningHoursDialog from "@/healthcenter/components/HealthcenterOpeningHoursDialog.vue";
import {ISO_WEEKDAYS} from "@/agenda/lib/openingHours";

type DAYS_NUMBER = typeof ISO_WEEKDAYS[number];

const {healthcenter} = defineProps<{ healthcenter: Healthcenter }>();
const emits = defineEmits<{
    (event: 'saved', opening: HealthcenterOpening | null): void;
}>();
const openings = computed<HealthcenterOpening | null>(() => healthcenter.openings?.[0] ?? null);
const dialogOpen = ref<boolean>(false);

const formattedHours = computed(() => {
    if (!openings.value) {
        return [{days: [], hours: {}}];
    }

    return groupHours(mapHours(openings.value));
});

/**
 * Check that the hours are the same.
 *
 * @param {OpeningHourObject} hours1 - First opening hours to compare.
 * @param {OpeningHourObject} hours2 - Second opening hours to compare.
 *
 * @return {boolean}
 */
const isEqual = (hours1: OpeningHourObject, hours2: OpeningHourObject): boolean => {
    return (hours1.om === hours2.om &&
        hours1.cm === hours2.cm &&
        hours1.oa === hours2.oa &&
        hours1.ca === hours2.ca
    );
};

/**
 * Given a given day, map the opening hours to match the request format.
 *
 * @param {number} day - The day of the week.
 * @param {HealthcenterOpening} opening - The opening hours.
 * @return {{[p: string]: string | null}}
 */
const getRequestForDay = (day: number, opening?: HealthcenterOpening): { [p: string]: string | null; } => {
    const _opening = opening?.openings.find((opening) => opening.day === day);

    return {
        [`d${day}_o_m`]: _opening?.hours.morning.opening ?? null,
        [`d${day}_c_m`]: _opening?.hours.morning.closing ?? null,
        [`d${day}_o_a`]: _opening?.hours.afternoon.opening ?? null,
        [`d${day}_c_a`]: _opening?.hours.afternoon.closing ?? null
    };
};

/**
 * Transform the opening hours to match the request format.
 *
 * @param {HealthcenterOpening} hours - The opening hours.
 * @return {OpeningHoursRequest}
 */
const mapHours = (hours?: HealthcenterOpening): OpeningHoursRequest => {
    return {
        ...getRequestForDay(1, hours),
        ...getRequestForDay(2, hours),
        ...getRequestForDay(3, hours),
        ...getRequestForDay(4, hours),
        ...getRequestForDay(5, hours),
        ...getRequestForDay(6, hours),
        ...getRequestForDay(7, hours)
    };
};


/**
 * Group the opening hours, if there are the same on multiple days.
 *
 * @param {LegacyHealthcenterOpening} hours - The opening hours received from the server.
 *
 * @return {any[]}
 */
const groupHours = (hours: OpeningHoursRequest): any[] => {
    const sortedHours: any[] = [];
    ISO_WEEKDAYS.forEach((day) => {
        if (day === 1) {
            sortedHours.push({
                days: [1],
                hours: {
                    om: hours?.d1_o_m?.substr(0, 5),
                    cm: hours?.d1_c_m?.substr(0, 5),
                    oa: hours?.d1_o_a?.substr(0, 5),
                    ca: hours?.d1_c_a?.substr(0, 5)
                }
            });
        } else {
            const hoursObject: any = {
                om: hours[`d${day}_o_m`]?.substr(0, 5),
                cm: hours[`d${day}_c_m`]?.substr(0, 5),
                oa: hours[`d${day}_o_a`]?.substr(0, 5),
                ca: hours[`d${day}_c_a`]?.substr(0, 5)
            };
            const hoursIndex = sortedHours.findIndex(hour => isEqual(hour.hours, hoursObject));

            if (hoursIndex !== -1) {
                sortedHours[hoursIndex].days.push(day);
            } else {
                sortedHours.push({
                    days: [day],
                    hours: hoursObject
                });
            }
        }
    });

    return sortedHours;
};

/**
 * Display the opening hours in a more readable way for the user.
 *
 * @param set - Set of days that match a range of opening hours.
 *
 * @return {string}
 */
const displayHours = (set: any): string => {
    dayjs.extend(localeData).locale('fr');
    const weekdays = dayjs.weekdays(true);
    const selectedDays = weekdays.filter((weekday, index) => !!set.days.find((day: DAYS_NUMBER) => day === index + 1));

    if (set.days.length === 0) {
        return "Saisir les horaires";
    }

    if (!set.hours.cm && !set.hours.oa && (!!set.hours.oa || !!set.hours.ca)) {
        return `• Ouvert ${selectedDays.join(', ')} de ${set.hours.om} à ${set.hours.ca}`;
    } else if ((!set.hours.om || !set.hours.cm) && (!set.hours.oa || !set.hours.ca)) {
        return `• Fermé ${selectedDays.join(', ')}`;
    } else if (!set.hours.om || !set.hours.cm) {
        return `• Ouvert ${selectedDays.join(', ')} de ${set.hours.oa} à ${set.hours.ca}`;
    } else if (!set.hours.oa || !set.hours.ca) {
        return `• Ouvert ${selectedDays.join(', ')} de ${set.hours.om} à ${set.hours.cm}`;
    }

    return `• Ouvert ${selectedDays.join(', ')} de ${set.hours.om} à ${set.hours.cm} ` +
        `et de ${set.hours.oa} à ${set.hours.ca}`;
};

</script>

<template>
  <div class="relative z-20 cursor-pointer" @click="dialogOpen = true">
    <Label class="text-sm">Heures d'ouvertures</Label>
    <div class="flex flex-row items-center gap-2">
      <div class="flex flex-col">
        <div v-for="(formattedHour, index) in formattedHours" :key="index" class="p-1">
          <p class="text-sm">
            {{ displayHours(formattedHour) }}
          </p>
        </div>
      </div>
      <icon name="mdi-pencil" color="primary" />
    </div>
  </div>
  <HealthcenterOpeningHoursDialog
    v-model:open="dialogOpen"
    :healthcenter-id="healthcenter.id"
    :opening="openings"
    @saved="opening => emits('saved', opening)"
  />
</template>
